if (!customElements.get('news-events-gallery')) {
  class NewsEventsGallery extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-year-tab]')];
      this.panels = [...this.querySelectorAll('[data-year-panel]')];
      this.dialog = this.querySelector('.cecile-news__lightbox');
      this.lightboxStage = this.querySelector('[data-lightbox-stage]');
      this.lightboxImage = new Image();
      this.lightboxImage.className = 'cecile-news__lightbox-image';
      this.lightboxStage?.append(this.lightboxImage);
      this.previousButton = this.querySelector('[data-lightbox-previous]');
      this.nextButton = this.querySelector('[data-lightbox-next]');
      this.closeButton = this.querySelector('[data-lightbox-close]');
      this.currentIndex = 0;
      this.lastTrigger = null;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(index));
        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index));
      });

      this.querySelectorAll('[data-lightbox-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.openLightbox(trigger));
      });

      this.previousButton?.addEventListener('click', () => this.showRelativeImage(-1));
      this.nextButton?.addEventListener('click', () => this.showRelativeImage(1));
      this.closeButton?.addEventListener('click', () => this.dialog.close());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.showRelativeImage(-1);
        if (event.key === 'ArrowRight') this.showRelativeImage(1);
      });
      this.dialog?.addEventListener('close', () => this.closeLightbox());

      this.carousels = [...this.querySelectorAll('[data-carousel]')].map((carousel) => this.setupCarousel(carousel));
    }

    setupCarousel(carousel) {
      const track = carousel.querySelector('[data-carousel-track]');
      const nav = carousel.querySelector('[data-carousel-nav]');
      const previous = carousel.querySelector('[data-carousel-previous]');
      const next = carousel.querySelector('[data-carousel-next]');
      const progress = carousel.querySelector('[data-carousel-progress]');

      const update = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        const scrollable = maxScroll > 2;
        nav.hidden = !scrollable;
        if (!scrollable) return;

        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll - 2;

        const thumb = Math.max(12, (track.clientWidth / track.scrollWidth) * 100);
        const ratio = track.scrollLeft / maxScroll;
        progress.style.setProperty('--carousel-thumb', `${thumb}%`);
        progress.style.setProperty('--carousel-offset', `${ratio * ((100 - thumb) / thumb) * 100}%`);
      };

      const scrollByPage = (direction) => {
        track.scrollBy({ left: direction * track.clientWidth * 0.85 });
      };

      previous.addEventListener('click', () => scrollByPage(-1));
      next.addEventListener('click', () => scrollByPage(1));
      track.addEventListener('scroll', update, { passive: true });
      track.addEventListener('scrollend', update);
      track.addEventListener('keydown', (event) => {
        if (event.target !== track) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollByPage(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollByPage(1);
        }
      });
      track.querySelectorAll('img').forEach((image) => image.addEventListener('load', update));
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      update();

      return { carousel, update };
    }

    activateTab(index) {
      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive.toString());
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });

      this.carousels?.forEach(({ carousel, update }) => {
        if (!carousel.closest('[hidden]')) update();
      });
    }

    handleTabKeydown(event, index) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();

      let nextIndex = index;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      this.activateTab(nextIndex);
      this.tabs[nextIndex].focus();
    }

    get activeTriggers() {
      const activePanel = this.panels.find((panel) => !panel.hidden);
      return activePanel ? [...activePanel.querySelectorAll('[data-lightbox-trigger]')] : [];
    }

    openLightbox(trigger) {
      const triggers = this.activeTriggers;
      this.currentIndex = Math.max(0, triggers.indexOf(trigger));
      this.lastTrigger = trigger;
      this.updateLightbox(triggers);
      this.dialog.showModal();
      document.body.classList.add('cecile-lightbox-open');
    }

    showRelativeImage(offset) {
      const triggers = this.activeTriggers;
      if (triggers.length < 2) return;
      this.currentIndex = (this.currentIndex + offset + triggers.length) % triggers.length;
      this.updateLightbox(triggers);
    }

    updateLightbox(triggers) {
      const trigger = triggers[this.currentIndex];
      if (!trigger) return;

      this.lightboxImage.src = trigger.dataset.fullImage;
      this.lightboxImage.alt = trigger.dataset.imageAlt || '';
      const hasMultipleImages = triggers.length > 1;
      this.previousButton.hidden = !hasMultipleImages;
      this.nextButton.hidden = !hasMultipleImages;
    }

    closeLightbox() {
      document.body.classList.remove('cecile-lightbox-open');
      this.lastTrigger?.focus();
    }
  }

  customElements.define('news-events-gallery', NewsEventsGallery);
}